"use client";

import { openWhatsApp, whatsappMessages } from "@/utils/whatsapp";
import { trackWhatsAppClick } from "@/utils/gtm";

export type WhatsAppButtonVariant = "solid" | "outline" | "hero-outline" | "icon-only" | "text-icon";
export type WhatsAppButtonSize = "sm" | "md" | "lg";

export interface WhatsAppButtonProps {
  message?: string;
  variant?: WhatsAppButtonVariant;
  size?: WhatsAppButtonSize;
  label?: string;
  className?: string;
  context?: string;
}

const VARIANT_STYLES: Record<WhatsAppButtonVariant, string> = {
  solid: "bg-[#25D366] text-white rounded-lg hover:bg-[#1ea855] transition-colors",
  outline: "border-2 border-[#25D366] text-[#25D366] rounded-lg bg-transparent hover:bg-[#25D366] hover:text-white transition-all",
  "hero-outline": "border border-white/80 bg-white/5 text-white rounded-lg backdrop-blur-sm hover:bg-white hover:text-[#128C7E] transition-all duration-200",
  "icon-only": "w-10 h-10 rounded-full bg-[#25D366] text-white hover:bg-[#1ea855] transition-colors",
  "text-icon": "text-[#25D366] bg-transparent hover:underline",
};

const SIZE_STYLES: Record<WhatsAppButtonSize, string> = {
  sm: "text-sm px-3 py-1.5",
  md: "text-base px-4 py-2",
  lg: "text-lg px-6 py-3",
};

function WhatsAppIcon({ className = "w-5 h-5 flex-shrink-0" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function getMessageType(message: string): string {
  if (message === whatsappMessages.default) return "default";
  if (message === whatsappMessages.counseling) return "counseling";
  if (message === whatsappMessages.jobs) return "jobs";
  if (message === whatsappMessages.corporate) return "corporate";
  if (typeof message === "string" && message.startsWith("Hi, I'm interested in the ") && message.includes(" course at Seasoft Classes")) return "course";
  if (typeof message === "string" && message.startsWith("Hi, I'd like to enroll in the ")) return "enroll";
  if (typeof message === "string" && message.startsWith("Hi, I read your article")) return "blog";
  return "custom";
}

export default function WhatsAppButton({
  message = whatsappMessages.default,
  variant = "solid",
  size = "md",
  label = "Chat on WhatsApp",
  className = "",
  context,
}: WhatsAppButtonProps) {
  const onClick = () => {
    openWhatsApp(message);
    trackWhatsAppClick(context || "whatsapp_button", getMessageType(message));
  };

  const isIconOnly = variant === "icon-only";
  const baseClasses = isIconOnly
    ? "inline-flex items-center justify-center"
    : "inline-flex items-center justify-center gap-2 font-bold";

  const variantClass = VARIANT_STYLES[variant] || VARIANT_STYLES.solid;
  const sizeClass = isIconOnly ? "" : (SIZE_STYLES[size] || SIZE_STYLES.md);

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${baseClasses} ${variantClass} ${sizeClass} ${className}`.trim()}
      aria-label={isIconOnly ? label : undefined}
    >
      <WhatsAppIcon className={isIconOnly ? "w-5 h-5" : "w-5 h-5 flex-shrink-0"} />
      {!isIconOnly && <span>{label}</span>}
    </button>
  );
}

